import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import {
  useGetQuotes,
  useGetPublicLibraryCount,
  getGetPublicLibraryCountQueryKey,
  useGetTags,
} from "@workspace/api-client-react";
import { AuthModal } from "@/components/AuthModal";
import { QuoteCard } from "@/components/QuoteCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { storePendingSave } from "@/lib/pendingSave";
import { usePlausible } from "@/hooks/usePlausible";

type AuthMode = "sign-in" | "sign-up";

/** How many quote cards between each interstitial CTA. */
const CTA_INTERVAL = 12;

/** Page size — a multiple of CTA_INTERVAL for clean interstitial placement. */
const PAGE_SIZE = 24;

// ── Feature list (used in the collapsible banner) ─────────────────────────
const FEATURES = [
  {
    icon: "☀️",
    title: "Daily Quote",
    description:
      "A fresh quote chosen just for you each morning — a personal reflection to start the day.",
  },
  {
    icon: "✦",
    title: "AI-Powered Discovery",
    description:
      "Save quotes you love and we'll surface others that match your taste across philosophy, literature, and science.",
  },
  {
    icon: "♥",
    title: "Personal Library",
    description:
      "Favorite quotes you want to keep close. Organized, searchable, and yours forever.",
  },
  {
    icon: "↗",
    title: "Share Beautifully",
    description:
      "Turn any quote into a wallpaper or a shareable link — send words that matter to people who matter.",
  },
];

// ── Quote shape expected by QuoteCard ────────────────────────────────────
interface CardQuote {
  id: number;
  text: string;
  author: string;
  era?: string | null;
  year?: string | null;
  tags: string[];
  score?: number | null;
}

/** Maps the raw API quote (tags are objects) into QuoteCard's flat shape. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toCardQuote(q: any): CardQuote {
  return {
    id: q.id,
    text: q.text,
    author: q.author,
    era: q.era,
    year: q.year,
    tags: Array.isArray(q.tags)
      ? q.tags.map((t: { name: string } | string) =>
          typeof t === "string" ? t : t.name,
        )
      : [],
    score: q.score ?? null,
  };
}

// ── Grid item model ───────────────────────────────────────────────────────
type GridItem =
  | { type: "quote"; quote: CardQuote }
  | { type: "cta"; key: number };

/**
 * Interleaves interstitial CTA cards after every CTA_INTERVAL quotes.
 * A trailing CTA (when the quotes end exactly on the interval) is suppressed.
 */
function buildGridItems(quotes: CardQuote[]): GridItem[] {
  const items: GridItem[] = [];
  for (let i = 0; i < quotes.length; i++) {
    items.push({ type: "quote", quote: quotes[i] });
    if ((i + 1) % CTA_INTERVAL === 0 && i + 1 < quotes.length) {
      items.push({ type: "cta", key: i });
    }
  }
  return items;
}

// ── Sub-components ────────────────────────────────────────────────────────

function InterstitialCTA({
  onSignUp,
  onSignIn,
}: {
  onSignUp: () => void;
  onSignIn: () => void;
}) {
  return (
    <div className="md:col-span-2 rounded-3xl border border-stone-200 bg-stone-900 px-8 py-10 text-white flex flex-col sm:flex-row items-center gap-8">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-2">
          Your personal library awaits
        </p>
        <h3 className="font-serif text-2xl sm:text-3xl text-white leading-snug">
          Save quotes. Train your taste. Discover more.
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-stone-400 max-w-md">
          Every quote you save teaches QuotedsArrow more about what resonates
          with you — and helps surface the next one.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
        <button
          onClick={onSignUp}
          className="rounded-2xl bg-white px-6 py-3 text-sm font-semibold text-stone-900 shadow-sm transition hover:bg-stone-100 active:scale-[0.98] whitespace-nowrap"
        >
          Create free account
        </button>
        <button
          onClick={onSignIn}
          className="text-sm text-stone-400 hover:text-stone-200 transition whitespace-nowrap"
        >
          Already a member? Sign in →
        </button>
      </div>
    </div>
  );
}

function WelcomeBanner({
  quoteCountLabel,
  onSignUp,
  onSignIn,
}: {
  quoteCountLabel: string | null;
  onSignUp: () => void;
  onSignIn: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-stone-200 bg-white">
      {/* Compact row — always visible */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-stone-600 flex items-center gap-2">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-stone-900 text-white text-xs font-bold shrink-0">
            ✦
          </span>
          <span>
            {quoteCountLabel !== null && (
              <><span className="font-medium text-stone-900">{quoteCountLabel}</span>{" "}</>
            )}
            curated literary quotes — browse freely, no account required.
          </span>
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onSignUp}
            className="rounded-2xl bg-stone-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-stone-800 active:scale-[0.98] whitespace-nowrap"
          >
            Create account — free
          </button>
          <button
            onClick={onSignIn}
            className="rounded-2xl border border-stone-200 px-4 py-2 text-xs font-medium text-stone-600 transition hover:border-stone-300 hover:text-stone-900 whitespace-nowrap"
          >
            Sign in
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 text-xs text-stone-400 hover:text-stone-600 transition whitespace-nowrap"
            aria-expanded={expanded}
          >
            {expanded ? "Less ↑" : "Learn more ↓"}
          </button>
        </div>
      </div>

      {/* Expandable feature panel */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          expanded ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
        aria-hidden={!expanded}
      >
        <div className="border-t border-stone-100 bg-stone-50 px-4 py-10 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <p className="text-sm leading-relaxed text-stone-500 mb-8 max-w-2xl">
              QuotedsArrow is a literary quote discovery app. Save quotes that
              resonate and our AI builds a taste profile — so every recommendation
              gets sharper over time. Browse 34 000+ quotes from philosophy,
              poetry, science, and history; all public-domain verified.
            </p>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {FEATURES.map((f) => (
                <div
                  key={f.title}
                  className="rounded-2xl border border-stone-200 bg-white p-5"
                >
                  <div className="mb-3 text-2xl">{f.icon}</div>
                  <h3 className="mb-1.5 text-sm font-semibold text-stone-900">
                    {f.title}
                  </h3>
                  <p className="text-xs leading-relaxed text-stone-500">
                    {f.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export function LandingPage() {
  usePageMeta({ title: "QuotedsArrow — Literary Quote Discovery" });
  const { trackEvent } = usePlausible();

  // ── Auth modal state ────────────────────────────────────────────────
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>("sign-up");
  const [authCustomHeading, setAuthCustomHeading] = useState<
    string | undefined
  >();
  const [authCustomBody, setAuthCustomBody] = useState<string | undefined>();

  // ── Quote grid state ────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [allQuotes, setAllQuotes] = useState<CardQuote[]>([]);

  // Stable random seed — deterministic grid order for the session, avoids
  // reshuffling when the user loads more or changes filters.
  const [seed] = useState(() => Math.floor(Math.random() * 100_000));

  const [selectedTag, setSelectedTag] = useState<string | undefined>();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input at 400 ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset accumulated list and page when filters change
  const prevFiltersRef = useRef({ tag: selectedTag, q: debouncedSearch });
  useEffect(() => {
    const prev = prevFiltersRef.current;
    if (prev.tag !== selectedTag || prev.q !== debouncedSearch) {
      setAllQuotes([]);
      setPage(1);
      prevFiltersRef.current = { tag: selectedTag, q: debouncedSearch };
    }
  }, [selectedTag, debouncedSearch]);

  // ── Data fetching ────────────────────────────────────────────────────
  // Paginated public quotes
  const { data: quotesPage, isLoading, isFetching } = useGetQuotes(
    {
      page,
      pageSize: PAGE_SIZE,
      status: "approved",
      seed,
      ...(selectedTag ? { tag: selectedTag } : {}),
      ...(debouncedSearch ? { query: debouncedSearch } : {}),
    },
  );
  // Independent of browsing filters; refetch on every visit, without loading quotes.
  const { data: libraryCount, isError: libraryCountError } = useGetPublicLibraryCount({
    query: {
      queryKey: getGetPublicLibraryCountQueryKey(),
      staleTime: 0,
      refetchOnMount: "always",
    },
  });
  const quoteCountLabel = libraryCountError || libraryCount === undefined
    ? null
    : libraryCount.total.toLocaleString("en-US");

  // Accumulate pages into allQuotes (dedup by id)
  useEffect(() => {
    if (!quotesPage?.data) return;
    const incoming = quotesPage.data.map(toCardQuote);
    if (page === 1) {
      setAllQuotes(incoming);
    } else {
      setAllQuotes((prev) => {
        const seen = new Set(prev.map((q) => q.id));
        return [...prev, ...incoming.filter((q) => !seen.has(q.id))];
      });
    }
  }, [quotesPage, page]);

  // Tags (top 10 by count)
  const { data: rawTags } = useGetTags();
  const topTags = [...(rawTags ?? [])]
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
    .slice(0, 10);

  // ── Handlers ─────────────────────────────────────────────────────────
  function openSignIn() {
    setAuthMode("sign-in");
    setAuthCustomHeading(undefined);
    setAuthCustomBody(undefined);
    setAuthOpen(true);
  }

  function openSignUp() {
    setAuthMode("sign-up");
    setAuthCustomHeading(undefined);
    setAuthCustomBody(undefined);
    setAuthOpen(true);
  }

  function handleGuestSave(quoteId: number) {
    storePendingSave(quoteId);
    setAuthMode("sign-up");
    setAuthCustomHeading("Save this quote");
    setAuthCustomBody(
      "Create a free account to save this quote and build your personal library. It takes less than a minute.",
    );
    setAuthOpen(true);
    trackEvent("Sign Up");
  }

  const hasMore = allQuotes.length < (quotesPage?.total ?? 0);
  const gridItems = buildGridItems(allQuotes);

  // ── Chip helpers ──────────────────────────────────────────────────────
  const chipBase =
    "rounded-full border px-3 py-1.5 text-xs font-medium transition whitespace-nowrap";
  const chipActive =
    "border-stone-900 bg-stone-900 text-white";
  const chipIdle =
    "border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:text-stone-900";

  return (
    <div className="min-h-screen bg-stone-50 font-sans">
      {/* ── Nav ──────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 border-b border-stone-200 bg-stone-50/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-stone-900 text-white text-sm font-bold">
              →
            </div>
            <span className="font-serif text-lg text-stone-900">
              QuotedsArrow
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openSignUp}
              className="rounded-2xl bg-stone-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-stone-800 active:scale-[0.98]"
            >
              Get started free
            </button>
            <button
              onClick={openSignIn}
              className="rounded-2xl border border-stone-200 bg-white px-4 py-2 text-xs font-medium text-stone-600 transition hover:border-stone-300 hover:text-stone-900"
            >
              Log in
            </button>
          </div>
        </div>
      </header>

      {/* ── Welcome banner ───────────────────────────────────────── */}
      <WelcomeBanner
        quoteCountLabel={quoteCountLabel}
        onSignUp={openSignUp}
        onSignIn={openSignIn}
      />

      {/* ── Browse section ───────────────────────────────────────── */}
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        {/* Heading + search */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="font-serif text-2xl text-stone-900 sm:text-3xl">
            Browse the library
          </h1>
          <div className="relative w-full sm:w-72">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">
              ⌕
            </span>
            <input
              type="search"
              placeholder="Search quotes or authors…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-2xl border border-stone-200 bg-white py-2.5 pl-8 pr-4 text-sm text-stone-900 placeholder:text-stone-400 transition focus:border-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-200"
            />
          </div>
        </div>

        {/* Tag filter chips */}
        {topTags.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedTag(undefined)}
              className={`${chipBase} ${selectedTag === undefined ? chipActive : chipIdle}`}
            >
              All
            </button>
            {topTags.map((tag) => (
              <button
                key={tag.name}
                onClick={() =>
                  setSelectedTag((t) =>
                    t === tag.name ? undefined : tag.name,
                  )
                }
                className={`${chipBase} ${selectedTag === tag.name ? chipActive : chipIdle}`}
              >
                {tag.name}
              </button>
            ))}
          </div>
        )}

        {/* Quote grid */}
        {isLoading && allQuotes.length === 0 ? (
          <div className="grid gap-5 md:grid-cols-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-3xl border border-stone-200 bg-white h-52 shadow-sm"
              />
            ))}
          </div>
        ) : allQuotes.length === 0 ? (
          <div className="rounded-3xl border border-stone-200 bg-white p-12 text-center shadow-sm">
            <p className="text-stone-400 text-sm">
              No quotes found
              {debouncedSearch ? ` for "${debouncedSearch}"` : ""}
              {selectedTag ? ` in #${selectedTag}` : ""}.
            </p>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            {gridItems.map((item) =>
              item.type === "cta" ? (
                <InterstitialCTA
                  key={`cta-${item.key}`}
                  onSignUp={openSignUp}
                  onSignIn={openSignIn}
                />
              ) : (
                <QuoteCard
                  key={item.quote.id}
                  quote={item.quote}
                  onGuestSave={handleGuestSave}
                  enableFlip
                />
              ),
            )}
          </div>
        )}

        {/* Load more */}
        {hasMore && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={isFetching}
              className="rounded-2xl border border-stone-200 bg-white px-8 py-3 text-sm font-medium text-stone-700 shadow-sm transition hover:border-stone-300 hover:bg-stone-50 active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
            >
              {isFetching ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-stone-300 border-t-stone-700" />
                  Loading…
                </span>
              ) : (
                "Load more quotes"
              )}
            </button>
          </div>
        )}
      </main>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <footer className="mt-12 border-t border-stone-800 bg-stone-900 py-8">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-stone-700 text-white text-sm font-bold">
                →
              </div>
              <span className="font-serif text-stone-400 text-sm">
                QuotedsArrow
              </span>
            </div>
            <div className="flex items-center gap-4">
              <p className="text-xs text-stone-600">Words with Pointed Purpose.</p>
              <Link
                href="/privacy"
                className="text-xs text-stone-500 hover:text-stone-300 transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="text-xs text-stone-500 hover:text-stone-300 transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* ── Auth modal ────────────────────────────────────────────── */}
      <AuthModal
        isOpen={authOpen}
        initialMode={authMode}
        onClose={() => setAuthOpen(false)}
        customHeading={authCustomHeading}
        customBody={authCustomBody}
      />
    </div>
  );
}
